import React from "react";
import { graphql } from "react-relay";
import { useRefetchableFragment } from "react-relay";

import { noop } from "lodash";

import { ChaptersPanel, type ChaptersPanelRef } from "~/meetingRecorder/ChaptersPanel";
import type { CopyRecapOptions } from "~/meetingRecorder/copyUtils";
import type { RecapSection } from "~/meetingRecorder/copyUtils";
import { copyRecapSection } from "~/meetingRecorder/copyUtils";
import { Highlights } from "~/meetingRecorder/HighlightsPanel";
import { RecordingContext } from "~/meetingRecorder/TranscriptModalContext";
import type { HighlightFeedbackSource } from "~/meetingRecorder/utils";

import type { MeetingContent_note$data } from "~/__generated__/MeetingContent_note.graphql";
import type { MeetingContent_viewer$data } from "~/__generated__/MeetingContent_viewer.graphql";
import type { MeetingInternalContent_meetingRecording$key } from "~/__generated__/MeetingInternalContent_meetingRecording.graphql";

import styles from "./MeetingContent.module.css";

type Props = {
	note: MeetingContent_note$data;
	viewer: MeetingContent_viewer$data;
	meetingRecording: MeetingInternalContent_meetingRecording$key;
	duration: number | null;
	onTimestampClick: (timestamp: number) => void;
	onRegenerateClicked: (source: HighlightFeedbackSource) => Promise<void>;
	openClipCutModal: (initialTimestamp?: number, initialEndTimestamp?: number) => void;
};

export default function MeetingInternalContent({
	meetingRecording: meetingRecordingKey,
	note,
	viewer,
	duration,
	onTimestampClick,
	onRegenerateClicked,
	openClipCutModal,
}: Props) {
	const [meetingRecording, _refetch] = useRefetchableFragment(
		graphql`
			fragment MeetingInternalContent_meetingRecording on MeetingRecording
			@refetchable(queryName: "MeetingInternalContentQuery") {
				__typename
				id
				title
				url
				subtitlesVttUrl
				meetingRecordedVideoDuration
				transcript
				activeSummary {
					id
					summary
					chapters {
						timestamp
						title
						bulletPoints {
							text
							timestamp
							textShort
							context
						}
					}
					actionItems {
						timestamp
						text
						assignees {
							userId
							fullName
						}
					}
					decisions {
						timestamp
						text
					}
					activeRecapTemplate
					templatedRecaps
				}
				clips {
					id
					clipStart
					clipEnd
					...ClipsPanel_ClipSummary_clip
				}
				isGeneratingRecapTemplate
				redactionIsInProgress
				regenerationForLanguageIsInProgress
				hasAskCopilotConversation
				thumbnailVttUrl
				meetingCallStart
				meetingRecordedVideoUrl
				recordingAndTranscriptWillBeDeleted
				transcriptDeletionReason
				meetingRecordingVideoDeletionReason
				recordingDataAutoDeletionDate
				userVideoProgress
				ownerAccount {
					__typename
					id
				}
				note {
					title
				}
				...ClipCutModal_meetingRecording
				...Timeline_meetingKeyEvents
				...HighlightsPanel_meetingRecording @arguments(public: false)
				...ChaptersPanel_meetingRecording
				...TranscriptVideo_meetingRecording @arguments(public: false)
				...ClipsPanel_meetingRecording
				...TranscriptPanel_Transcript_meetingRecording @arguments(public: false)
				...DislikeModal_meetingRecording
				...TranscriptModalContentStats
				...TranscriptModalGenerating_RedactingPreview_meetingRecording
			}
		`,
		meetingRecordingKey,
	);

	const chaptersRef = React.useRef<ChaptersPanelRef>(null); // TODO: only for compatability

	const copyRecordingSection = React.useCallback(
		async (section: RecapSection, options?: CopyRecapOptions) => {
			await copyRecapSection(meetingRecording, section, options);
		},
		[meetingRecording],
	);

	const recordingContextValue = React.useMemo(
		() => ({
			recordingId: meetingRecording.id,
			copySection: copyRecordingSection,
			openClipCutModal,
		}),
		[meetingRecording, copyRecordingSection, openClipCutModal],
	);

	if (!meetingRecording) {
		return null;
	}

	const highlightsEl = (
		<article key="highlights-el">
			<Highlights
				note={note}
				meetingRecording={meetingRecording}
				viewer={viewer}
				videoDuration={duration}
				onTimestampClick={onTimestampClick}
				onDislike={onRegenerateClicked}
				onGenerateTemplate={noop}
				chaptersRef={chaptersRef}
				thumbnailTrack={null}
				narrowViewport={false}
				aiDetectedActionItemsListRefetchKey={0}
				trackLowLanguageConfidenceScoreBannerShown={noop}
			/>
		</article>
	);

	const chaptersComponent = (
		<ChaptersPanel
			key="chapters-panel"
			ref={chaptersRef}
			recordingId={meetingRecording.id}
			chapters={meetingRecording.activeSummary?.chapters ?? []}
			onTimestampClick={onTimestampClick}
			onDislike={onRegenerateClicked}
			videoDuration={duration}
			thumbnailTrack={null}
			userVideoProgress={meetingRecording.userVideoProgress}
			mode="readonly"
			recordingTitle={meetingRecording.title}
			chaptersScrollDisabled={true}
		/>
	);

	return (
		<div className={styles.meetingInternalContent}>
			<RecordingContext.Provider value={recordingContextValue}>
				{highlightsEl}
				{chaptersComponent}
			</RecordingContext.Provider>
		</div>
	);
}
